import { Button, Form, Input, InputNumber, Modal, Radio, Select, Space } from "antd";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { ANSWER_TYPE } from "features/ProjectType/core";
import { buildQuestionPayload } from "features/ProjectType/data/apiAdapters";
import { useCreateProjectQuestion, useUpdateProjectQuestion } from "features/ProjectType/data/hooks";
import { ProjectTypeState, setFormApiErrors } from "features/ProjectType/presentation/components/ProjectTypeState";

export const ANSWER_TYPE_LABEL = {
  NUMBER: "Số",
  SHORT_TEXT: "Chữ ngắn",
  SINGLE_CHOICE: "Chọn một",
  MULTI_CHOICE: "Chọn nhiều",
  YES_NO: "Có/Không",
};
const Handle = SortableHandle(() => (
  <span aria-label="Kéo lựa chọn" style={{ cursor: "grab" }}>
    ⋮⋮
  </span>
));
const OptionRow = SortableElement(({ children }) => (
  <div className="project-type-option">
    <Handle />
    {children}
  </div>
));
const Options = SortableContainer(({ children }) => <div>{children}</div>);
export function validateOptions(_, values) {
  if (!values?.length || values.some((value) => !value?.trim() || value.trim().length > 100))
    return Promise.reject(new Error("Nhập ít nhất một lựa chọn, từ 1 đến 100 ký tự"));
  if (new Set(values.map((value) => value.trim().toLocaleLowerCase())).size !== values.length)
    return Promise.reject(new Error("Lựa chọn không được trùng nhau"));
  return Promise.resolve();
}
export default function QuestionFormModal({ current, projectTypeId, nextOrder, onSaved, onClose }) {
  const [form] = Form.useForm();
  const create = useCreateProjectQuestion();
  const update = useUpdateProjectQuestion();
  const mutation = current ? update : create;
  const save = async (values) => {
    try {
      const payload = buildQuestionPayload(values);
      if (current) await update.mutate(current.id, payload);
      else await create.mutate(projectTypeId, payload);
      await onSaved();
      onClose();
    } catch (error) {
      setFormApiErrors(form, error);
    }
  };
  return (
    <Modal
      className="project-type-modal"
      visible
      width={580}
      title={current ? "Sửa câu hỏi" : "Thêm câu hỏi"}
      okText="Lưu"
      cancelText="Hủy"
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={mutation.loading}
      cancelButtonProps={{ disabled: mutation.loading }}
      maskClosable={!mutation.loading}
      closable={!mutation.loading}
    >
      {mutation.error?.code === "FORBIDDEN" ? (
        <ProjectTypeState error={mutation.error} />
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={
            current || { answerType: ANSWER_TYPE.NUMBER, displayOrder: nextOrder, isRequired: false, options: [] }
          }
          onFinish={save}
          onValuesChange={(changed) => {
            if (changed.answerType) {
              const values = form.getFieldsValue();
              const clean = buildQuestionPayload({ ...values, label: values.label || "" });
              form.setFieldsValue({ unit: clean.unit, placeholder: clean.placeholder, options: clean.options });
            }
          }}
        >
          <Form.Item
            name="label"
            label="Tên câu hỏi"
            rules={[
              { required: true, whitespace: true, message: "Vui lòng nhập tên câu hỏi" },
              { max: 255, message: "Tối đa 255 ký tự" },
            ]}
          >
            <Input />
          </Form.Item>
          <Space align="start">
            <Form.Item name="answerType" label="Kiểu trả lời" rules={[{ required: true }]}>
              <Select
                style={{ width: 250 }}
                options={Object.entries(ANSWER_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
            <Form.Item
              name="displayOrder"
              label="Thứ tự hiển thị"
              rules={[
                { required: true, message: "Vui lòng nhập thứ tự" },
                { type: "integer", min: 0, message: "Thứ tự phải là số nguyên ≥0" },
              ]}
            >
              <InputNumber />
            </Form.Item>
          </Space>
          <Form.Item name="isRequired" label="Bắt buộc">
            <Radio.Group>
              <Radio value={true}>Bắt buộc</Radio>
              <Radio value={false}>Tùy chọn</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.answerType !== next.answerType}>
            {() => {
              const type = form.getFieldValue("answerType");
              return (
                <>
                  {type === ANSWER_TYPE.NUMBER && (
                    <Form.Item name="unit" label="Đơn vị" rules={[{ max: 50, message: "Tối đa 50 ký tự" }]}>
                      <Input />
                    </Form.Item>
                  )}
                  {[ANSWER_TYPE.NUMBER, ANSWER_TYPE.SHORT_TEXT].includes(type) && (
                    <Form.Item
                      name="placeholder"
                      label="Gợi ý nhập"
                      rules={[{ max: 255, message: "Tối đa 255 ký tự" }]}
                    >
                      <Input />
                    </Form.Item>
                  )}
                  {[ANSWER_TYPE.SINGLE_CHOICE, ANSWER_TYPE.MULTI_CHOICE].includes(type) && (
                    <Form.List name="options" rules={[{ validator: validateOptions }]}>
                      {(fields, { add, remove, move }, { errors }) => (
                        <>
                          <label>Danh sách lựa chọn *</label>
                          <Options
                            useDragHandle
                            onSortEnd={({ oldIndex, newIndex }) => {
                              if (oldIndex !== newIndex) move(oldIndex, newIndex);
                            }}
                            helperClass="project-type-drag-row"
                          >
                            {fields.map((field, index) => (
                              <OptionRow key={field.key} index={index}>
                                <Form.Item
                                  {...field}
                                  style={{ marginBottom: 0, flex: 1 }}
                                  rules={[
                                    { required: true, whitespace: true, message: "Vui lòng nhập lựa chọn" },
                                    { max: 100, message: "Tối đa 100 ký tự" },
                                  ]}
                                >
                                  <Input aria-label={`Lựa chọn ${index + 1}`} />
                                </Form.Item>
                                <Button aria-label={`Xóa lựa chọn ${index + 1}`} onClick={() => remove(field.name)}>
                                  ×
                                </Button>
                              </OptionRow>
                            ))}
                          </Options>
                          <Form.ErrorList errors={errors} />
                          <Button type="link" onClick={() => add("")}>
                            + Thêm lựa chọn
                          </Button>
                        </>
                      )}
                    </Form.List>
                  )}
                </>
              );
            }}
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
